import {
  ClientResponse,
  ConversationActionParams,
  ConversationEventType,
  ConversationInlineData,
  ConversationMessage,
  InlineDataConversationMessage,
  InlineDataResultAction,
  ConversationParticipant,
  SelectOptionAction,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessagePart,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ReportWarning, ErrorResponse, ApiMethods} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {emitMessagesWithAnimation, waitForAnimation} from './MessageAnimation';
import {PageContextNormalizer} from './PageContextNormalizer';
import {
  buildCachedAnsweredOptionsPromptMessage,
  buildDeltaResponseState,
  emitDeltaResponse,
  refetchConversationAndEmitDelta,
} from './SendResponseHandling';
import {asRecord, asString} from '../utils';

type ActionSendMessageResponse =
  | {type: 'success'; data: ConversationsApiDataConversation}
  | ErrorResponse;

type ActionDeleteConversationResponse =
  | {type: 'success'; data: undefined}
  | ErrorResponse;

type ActionHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
  ) => Promise<ActionSendMessageResponse>;
  getConversation?: (
    conversationId: string,
  ) => Promise<
    {type: 'success'; data: ConversationsApiDataConversation} | ErrorResponse
  >;
  deleteConversation?: (
    conversationId: string,
  ) => Promise<ActionDeleteConversationResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  reportWarning?: ReportWarning;
  getParticipant: () => ConversationParticipant | undefined;
  enableUhPendingInput?: boolean;
  attachmentDownloadPath?: string;
  getCachedMessage?: (
    conversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
};

export class ActionHandler {
  private readonly deps: ActionHandlerDeps;

  constructor(deps: ActionHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: ConversationActionParams): Promise<ClientResponse> => {
    const {action, conversationId} = params;

    if (action && action.name === 'select-option') {
      return this.handleSelectOption(
        conversationId,
        action as SelectOptionAction,
      );
    }
    if (action && action.name === 'inline-data-result') {
      return this.handleInlineDataResult(
        conversationId,
        action as InlineDataResultAction,
      );
    }

    return {code: 200};
  };

  private async handleSelectOption(
    conversationId: string,
    action: SelectOptionAction,
  ): Promise<ClientResponse> {
    const {message, selectedOptionId} = action;
    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const selectedOption = message.options.find(
      (option) => option.id === selectedOptionId,
    );
    if (!selectedOption) {
      this.deps.reportWarning?.('Unknown selected option', {
        tags: {
          selected_option_id: selectedOptionId,
        },
      });
      return {code: 400, error: `Unknown selected option: ${selectedOptionId}`};
    }

    this.deps.emitConversationEvent({
      name: 'conversation-option-selected',
      conversationId,
      optionId: selectedOptionId,
    });
    if (message.purpose) {
      this.deps.emitConversationEvent({
        name: 'conversation-set-context-values',
        conversationId,
        values: {[message.purpose]: selectedOptionId},
      });
    }

    await waitForAnimation();

    const payload = ActionHandler.buildSelectedOptionPayload({
      action,
      participant,
      selectedOption,
    });
    const response = await this.deps.sendMessage(conversationId, payload);
    if (response.type === 'error') {
      if (ActionHandler.shouldRefetchOnInvalidOption(response)) {
        await refetchConversationAndEmitDelta({
          getConversation: this.deps.getConversation,
          emitConversationEvent: this.deps.emitConversationEvent,
          getCachedMessage: this.deps.getCachedMessage,
          conversationId,
          participantsById: {[participant.id]: participant},
          user: participant,
          attachmentDownloadPath: this.deps.attachmentDownloadPath,
          deleteConversation: this.deps.deleteConversation,
          reportWarning: this.deps.reportWarning,
        });
      }
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const {
      context,
      ended,
      messages: newMessages,
    } = buildDeltaResponseState({
      conversationId,
      apiMessages: response.data.messages,
      participantsById: {[participant.id]: participant},
      user: participant,
      reportWarning: this.deps.reportWarning,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
    });
    const promptUpdate = buildCachedAnsweredOptionsPromptMessage({
      conversationId,
      creatorPartId: ActionHandler.buildCreatorPartId(message.id),
      getCachedMessage: this.deps.getCachedMessage,
      participant,
      selectedOptionId,
      fallbackMessage: message,
    });
    await emitDeltaResponse({
      emitConversationEvent: this.deps.emitConversationEvent,
      conversationId,
      promptUpdate,
      context,
      ended,
      deleteConversation: this.deps.deleteConversation,
      typingEndedParticipantId: participant.id,
      reportWarning: this.deps.reportWarning,
      emitMessages: () =>
        this.emitNewMessagesWithAnimation(conversationId, newMessages),
    });

    // todo(@danielxu): temporary — inject experiment variant into button-options message so
    // ChatInput can read it. Remove once the experiment is over.
    const temporaryExperimentVariant =
      response.data.temporary_experiment_variant;
    if (temporaryExperimentVariant) {
      const cachedOptionsMessage = this.deps.getCachedMessage?.(
        conversationId,
        message.id,
      );
      if (cachedOptionsMessage) {
        this.deps.emitConversationEvent({
          name: 'conversation-message-added',
          conversationId,
          participantId: cachedOptionsMessage.participant.id,
          messageId: cachedOptionsMessage.id,
          nonce: cachedOptionsMessage.nonce,
          message: {
            ...cachedOptionsMessage,
            experimentVariant: temporaryExperimentVariant,
          } as ConversationMessage,
        });
      }
    }

    return {code: 200};
  }

  private async handleInlineDataResult(
    conversationId: string,
    action: InlineDataResultAction,
  ): Promise<ClientResponse> {
    const {message, result} = action;
    if (message.inlineData.isDone) {
      return {code: 200};
    }

    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const outcome = ActionHandler.inlineDataOutcome(result);
    const dataWithPii = ActionHandler.inlineDataWithPii(result);
    const updatedInlineData = ActionHandler.buildUpdatedInlineData({
      inlineData: message.inlineData,
      result,
      outcome,
    });

    if (ActionHandler.isLocalOnlyInlineDataOutcome(outcome)) {
      this.emitInlineDataUpdate(conversationId, message, updatedInlineData);
      this.emitInlineDataContextUpdate(conversationId, outcome, result.extra);
      return {code: 200};
    }

    await waitForAnimation();

    const response = await this.deps.sendMessage(
      conversationId,
      ActionHandler.buildInlineDataResultPayload({
        message,
        participant,
        result,
        outcome,
        dataWithPii,
      }),
    );

    if (response.type === 'error') {
      if (ActionHandler.shouldRefetchOnInvalidOption(response)) {
        await refetchConversationAndEmitDelta({
          getConversation: this.deps.getConversation,
          emitConversationEvent: this.deps.emitConversationEvent,
          getCachedMessage: this.deps.getCachedMessage,
          conversationId,
          participantsById: {[participant.id]: participant},
          user: participant,
          attachmentDownloadPath: this.deps.attachmentDownloadPath,
          deleteConversation: this.deps.deleteConversation,
          reportWarning: this.deps.reportWarning,
        });
      }
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    this.emitInlineDataUpdate(conversationId, message, updatedInlineData);
    const {
      context,
      ended,
      messages: newMessages,
    } = buildDeltaResponseState({
      conversationId,
      apiMessages: response.data.messages,
      participantsById: {[participant.id]: participant},
      user: participant,
      reportWarning: this.deps.reportWarning,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
    });
    await emitDeltaResponse({
      emitConversationEvent: this.deps.emitConversationEvent,
      conversationId,
      context,
      ended,
      deleteConversation: this.deps.deleteConversation,
      typingEndedParticipantId: participant.id,
      reportWarning: this.deps.reportWarning,
      emitMessages: () =>
        this.emitNewMessagesWithAnimation(conversationId, newMessages),
    });

    return {code: 200};
  }

  private async emitNewMessagesWithAnimation(
    conversationId: string,
    messages: ConversationMessage[],
  ): Promise<void> {
    await emitMessagesWithAnimation({
      conversationId,
      emitConversationEvent: this.deps.emitConversationEvent,
      messages,
      waitForCompletion: this.deps.enableUhPendingInput === true,
    });
  }

  private static shouldRefetchOnInvalidOption(
    response: ErrorResponse,
  ): boolean {
    return response.code === 400 && response.message === undefined;
  }

  private static buildSelectedOptionPayload({
    action,
    participant,
    selectedOption,
  }: {
    action: SelectOptionAction;
    participant: ConversationParticipant;
    selectedOption: SelectOptionAction['message']['options'][number];
  }): ConversationsApiSendMessageEndpointRequest {
    const creatorPartId = ActionHandler.buildCreatorPartId(action.message.id);

    return {
      id: ApiMethods.generateMessageId(),
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts: [ActionHandler.buildSelectedOptionPart(selectedOption)],
      ...(creatorPartId ? {creator_part_id: creatorPartId} : {}),
      page_context: ActionHandler.buildPageContext(action.data),
    };
  }

  private static buildCreatorPartId(messagePartId: string): string | undefined {
    const separatorIndex = messagePartId.lastIndexOf('.');
    if (separatorIndex <= 0 || separatorIndex === messagePartId.length - 1) {
      return undefined;
    }

    return messagePartId;
  }

  private static buildSelectedOptionPart(
    selectedOption: SelectOptionAction['message']['options'][number],
  ): ConversationsApiDataMessagePart {
    return {
      id: 1,
      type: 'selected_option',
      content: {
        id: selectedOption.id,
        text: selectedOption.label,
      },
    };
  }

  private static buildInlineDataResultPayload({
    message,
    participant,
    result,
    outcome,
    dataWithPii,
  }: {
    message: InlineDataResultAction['message'];
    participant: ConversationParticipant;
    result: InlineDataResultAction['result'];
    outcome?: string;
    dataWithPii?: Record<string, unknown>;
  }): ConversationsApiSendMessageEndpointRequest {
    const creatorPartId = ActionHandler.inlineDataCreatorPartId(message);
    const parts: ConversationsApiDataMessagePart[] = [
      {
        id: 1,
        type: 'inline_data_outcome',
        content: {
          status: result.type === 'success' ? 'done' : 'error',
          text: result.message ?? '',
          ...(outcome ? {outcome} : {}),
        },
      },
    ];

    return {
      id: ApiMethods.generateMessageId(),
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts,
      ...(creatorPartId ? {creator_part_id: creatorPartId} : {}),
      ...(dataWithPii ? {data_with_pii: dataWithPii} : {}),
    };
  }

  private emitInlineDataUpdate(
    conversationId: string,
    message: InlineDataResultAction['message'],
    inlineData: ConversationInlineData,
  ): void {
    if (!ActionHandler.isInlineDataConversationMessage(message)) {
      return;
    }

    const updatedMessage: InlineDataConversationMessage = {
      ...message,
      inlineData,
    };
    this.deps.emitConversationEvent({
      name: 'conversation-message-updated',
      conversationId,
      participantId: message.participant.id,
      messageId: message.id,
      nonce: message.nonce,
      message: updatedMessage,
    });
  }

  private emitInlineDataContextUpdate(
    conversationId: string,
    outcome: 'channel_rac',
    extra?: Record<string, unknown>,
  ): void {
    const value = asRecord(extra?.value);
    if (!value) {
      return;
    }

    const racForm = asRecord(value.rac_form);
    this.deps.emitConversationEvent({
      name: 'conversation-set-context-values',
      conversationId,
      values: {
        channel: 'request_a_call',
        requestACallDetails: {
          userName: asString(racForm?.name),
          userPhoneNumber: asString(racForm?.phone_number),
          callCanceled: false,
        },
      },
    });
  }

  private static buildUpdatedInlineData({
    inlineData,
    result,
    outcome,
  }: {
    inlineData: ConversationInlineData;
    result: InlineDataResultAction['result'];
    outcome?: string;
  }): ConversationInlineData {
    let {data} = inlineData;
    const value = asRecord(result.extra?.value) ?? asRecord(result.data);
    if (value && ActionHandler.isArrayWithLength(inlineData.data, 1)) {
      data = inlineData.data.map((datum) => {
        const datumValue = asRecord(datum.value) ?? {};
        return {
          ...datum,
          value: {
            ...datumValue,
            ...value,
          },
        };
      });
    }

    return {
      ...inlineData,
      isDone: true,
      outcome: outcome ?? inlineData.outcome,
      data,
    };
  }

  private static inlineDataOutcome(
    result: InlineDataResultAction['result'],
  ): string | undefined {
    const extraOutcome = result.extra?.outcome;
    if (typeof extraOutcome === 'string' && extraOutcome.length > 0) {
      return extraOutcome;
    }

    const dataOutcome = result.data?.outcome;
    if (typeof dataOutcome === 'string' && dataOutcome.length > 0) {
      return dataOutcome;
    }

    return undefined;
  }

  private static inlineDataWithPii(
    result: InlineDataResultAction['result'],
  ): Record<string, unknown> | undefined {
    return (
      asRecord(result.extra?.dataWithPii) ?? asRecord(result.data?.dataWithPii)
    );
  }

  private static isLocalOnlyInlineDataOutcome(
    outcome?: string,
  ): outcome is 'channel_rac' {
    return outcome === 'channel_rac';
  }

  private static inlineDataCreatorPartId(
    message: InlineDataResultAction['message'],
  ): string | undefined {
    if (
      ActionHandler.isInlineDataConversationMessage(message) &&
      typeof message.id === 'string' &&
      message.id.length > 0
    ) {
      return message.id;
    }

    return undefined;
  }

  private static isInlineDataConversationMessage(
    message: InlineDataResultAction['message'],
  ): message is InlineDataConversationMessage {
    const isIdString = 'id' in message && typeof message.id === 'string';
    const isTimestampNumber =
      'timestamp' in message && typeof message.timestamp === 'number';
    const isParticipantPresent =
      'participant' in message && !!message.participant;

    return isIdString && isTimestampNumber && isParticipantPresent;
  }

  private static isArrayWithLength<T>(
    value: T,
    expectedLength: number,
  ): value is T extends readonly unknown[] ? T : never {
    if (!Array.isArray(value)) {
      return false;
    }
    return value.length === expectedLength;
  }

  private static buildPageContext(
    data?: Record<string, unknown>,
  ): ConversationsApiSendMessageEndpointRequest['page_context'] {
    const candidate = PageContextNormalizer.extractPageContextCandidate(data);
    if (!candidate) {
      return undefined;
    }

    const pageContext =
      PageContextNormalizer.normalizePageContextForMessage(candidate);
    return Object.keys(pageContext).length > 0
      ? (pageContext as ConversationsApiSendMessageEndpointRequest['page_context'])
      : undefined;
  }
}
